import { useState, useEffect, useContext, useRef } from 'react';
import Styles from './InternshipPackView.module.scss';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useDebounce, Message, AuthContext, ViewContext } from 'components/lib';

/* Global Components */
import SearchBar from 'components/global/SearchBar/SearchBar';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { BreadCrumbs } from 'components/global/BreadCrumbs/BreadCrumbs';
import ProductGridCard from 'components/global/ProductCards/ProductGridCard/ProductGridCard';
import ProductListCard from 'components/global/ProductCards/ProductListCard/ProductListCard';
import PurchaseGridCard from 'components/global/PurchaseCards/PurchaseGridCard/PurchaseGridCard';
import PurchaseListCard from 'components/global/PurchaseCards/PurchaseListCard/PurchaseListCard';

/* View-specific Components */
import { PaymentPending } from 'views/account/paymentPending';
import { Notification } from 'components/notifications/notification';

/* UI Libraries */
import { Tabs, Tab, Box, Pagination, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { GridView, ViewList, SearchOff, PlaylistAddCheck, AccountBalance, School } from '@mui/icons-material';
import { ToastContainer } from 'react-toastify';

export function InternshipPackView(props) {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const context = useContext(AuthContext);
    const viewContext = useContext(ViewContext)
    const [currentTab, setCurrentTab] = useState(searchParams.get('tab') || 'my-internships');
    const [viewType, setViewType] = useState('grid');
    const [searchValue, setSearchValue] = useState('');
    const [searchActive, setSearchActive] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [internships, setInternships] = useState([]);
    const [loading, setLoading] = useState(false);
    const debouncedSearchValue = useDebounce(searchValue, 500, 3);
    const [pageSize, setPageSize] = useState(12);

    const hasCalculated = useRef(false);
    const currentCols = useRef(0);
    const gridRef = useRef(null);

    // Reset hasInitialized when tab or viewType changes
    useEffect(() => {
        hasCalculated.current = false;
        currentCols.current = 0;
    }, [viewType, currentTab]);

    useEffect(() => {
        let cancelled = false;
        const fetchData = async () => {
            if (viewType === 'grid' && pageSize === 0) return;
            setLoading(true);
            try {
                let url;
                switch (currentTab) {
                    case 'my-internships':
                        url = '/api/user/internship';
                        break;
                    case 'purchase':
                        url = '/api/internship/available';
                        break;
                    case 'org':
                        url = '/api/internship/org';
                        break;
                    default:
                        url = '/api/user/internship';
                }

                const response = await axios.get(url, {
                    params: {
                        page: pageNumber,
                        pageSize: viewType === 'grid' ? pageSize : 10,
                        search: debouncedSearchValue.trim(),
                    }
                });

                if (cancelled) return;
                const data = response?.data?.data;
                setInternships(data);

                const totalCount = response?.data?.total || response?.data?.totalCount ||
                    (Array.isArray(data) ? data.length : 0);
                setTotalPages(Math.ceil(totalCount / pageSize));
            } catch (err) {
                if (!cancelled) viewContext.notification.show('Failed to fetch Internship Pack(s)', 'error');
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchData();

        return () => { cancelled = true };
    }, [
        currentTab,
        pageNumber,
        debouncedSearchValue,
        pageSize,
        viewType
    ]);

    useEffect(() => {
        const calculateColumns = () => {
            const container = gridRef.current;
            if (!container) return;

            const containerWidth = container.clientWidth;
            // get ONE card (even from small data)
            const card = container.querySelector(':scope > *');
            if (!card) return;

            const cardWidth = card.getBoundingClientRect().width;

            if (!cardWidth) return;

            const cols = Math.max(1, Math.floor(containerWidth / cardWidth));

            if (cols === currentCols.current) return;

            currentCols.current = cols;

            setPageSize(cols * 4);
        };

        const timer = setTimeout(calculateColumns, 100);

        window.addEventListener('resize', calculateColumns);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('resize', calculateColumns);
        };
    }, [viewType, internships.length]);

    useEffect(() => {
        setPageNumber(1)
    }, [pageSize])

    const handleTabChange = (event, newValue) => {
        setCurrentTab(newValue);
        setSearchParams({ tab: newValue });
        setPageNumber(1);
        setSearchValue('');
        setSearchActive(false);
    };

    const handlePageChange = (event, value) => {
        setPageNumber(value);
    };

    const handleViewChange = (event, nextView) => {
        if (nextView !== null) {
            setViewType(nextView);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        let cleanedText = pastedText
            .replace(/^\s+/, "")
            .replace(/\s{2,}/g, " ")
            .trim();

        if (cleanedText) {
            setSearchValue(cleanedText);
            setPageNumber(1);
        }
    };


    const handleCardClick = (internship) => {
        switch (currentTab) {
            case 'my-internships':
                navigate(`/internships/${internship.internship_id}/info`);
                break
            case 'purchase':
                navigate(`/internships/info/${internship.id}`)
                break
            case 'org':
                navigate(`/internships/info/${internship.id}`)
                break
            default:
                navigate(`/internships/${internship.internship_id}/info`);
        }
    };

    const renderLabCards = () => {
        const isMyInternships = currentTab === 'my-internships';
        const mode = currentTab === 'org' ? 'enroll' :
            (context?.user?.plan === 'free' || context?.user?.plan === 'hybrid') ? 'purchase' : 'enroll';
        return internships.map((internship) => {
            const isGrid = viewType === 'grid';
            if (isMyInternships) {
                const Card = isGrid ? ProductGridCard : ProductListCard;
                return (
                    <Card
                        data={internship}
                        key={internship.id}
                        type="internship"
                        onClick={handleCardClick}
                        button_text={internship.buttonText}
                        showProgressBar={true}
                        showInfoChips={true}
                        showMedalChips={false}
                        showBadge={true}
                        tooltipMessage={props.t('InternshipPack.InternshipPackView.tooltip')}
                    />
                );
            } else {
                const Card = isGrid ? PurchaseGridCard : PurchaseListCard;
                return (
                    <Card
                        key={internship.id}
                        data={internship}
                        onCardClick={handleCardClick}
                        mode={mode}
                        type="internship"
                        showBadge={true}
                    />
                );
            }
        });
    }

    return (
        <>
            <ToastContainer
                position="top-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
            <Notification call='internship' />
            {context?.user?.subscription === 'active' ? (
                context?.user?.onboarded ? (
                    <Message
                        closable
                        title={props.t('InternshipPack.InternshipPackView.message.title')}
                        text={props.t('InternshipPack.InternshipPackView.message.text')}
                        type="info"
                    />
                ) : (
                    <Message
                        title={props.t('InternshipPack.InternshipPackView.profilemessage.title')}
                        text={props.t('InternshipPack.InternshipPackView.profilemessage.text')}
                        type="warning"
                        buttonText={'Setup Profile'}
                        buttonLink={'/account'}
                    />
                )
            ) : (
                <PaymentPending />
            )}
            <div data-testid="InternshipPackView-cardContainer" className={Styles.cardContainer}>
                <div data-testid="InternshipPackView-tabContainer" className={Styles.tabContainer}>
                    <BreadCrumbs
                        onClickPaths={['']}
                        crumbs={['Internships']}
                    />
                </div>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tabs
                        value={currentTab}
                        onChange={handleTabChange}
                        aria-label="internships tabs"
                    >
                        <Tab label={props.t('InternshipPack.InternshipPackView.MyInternships')} value="my-internships" />
                        <Tab label={props.t('InternshipPack.InternshipPackView.AvailableInternships')} value="purchase" />
                        {context?.user?.plan === 'hybrid' && (
                            <Tab label={props.t('InternshipPack.InternshipPackView.OrganizationInternships')} value="org" />
                        )}
                    </Tabs>
                </Box>
                <div data-testid="InternshipPackView-searchBar-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '25px' }}>
                    <SearchBar
                        value={searchValue}
                        setValue={(val) => {
                            setSearchValue(val);
                            setPageNumber(1);
                            setSearchActive(val.length > 0);
                        }}
                        placeholder={props.t('InternshipPack.InternshipPackView.search.placeholder')}
                        searchActive={searchActive}
                        onClear={() => {
                            setSearchValue('');
                            setPageNumber(1);
                            setSearchActive(false);
                        }}
                        onPaste={handlePaste}
                    />
                    <ToggleButtonGroup
                        value={viewType}
                        exclusive
                        onChange={handleViewChange}
                        size="small"
                        color="primary"
                    >
                        <ToggleButton value="list" aria-label="list view">
                            <ViewList fontSize="medium" />
                        </ToggleButton>
                        <ToggleButton value="grid" aria-label="grid view">
                            <GridView fontSize="medium" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </div>
                {internships?.length > 0 && currentTab !== 'my-internships' && (
                    <Typography variant="h7" sx={{ display: 'flex', gap: '16px', padding: '24px 0px', mb: '-15px' }}>
                        {currentTab === 'org' ?
                            props.t('InternshipPack.InternshipPackView.info.enroll') :
                            (context?.user?.plan === 'free' || context?.user?.plan === 'hybrid') ?
                                props.t('InternshipPack.InternshipPackView.info.purchase') :
                                props.t('InternshipPack.InternshipPackView.info.enroll')
                        }
                    </Typography>
                )}

                {/* Cards */}
                {loading ? (
                    <LoadingBackdrop isOpen={true} />
                ) : internships.length > 0 ? (
                    <div data-testid="InternshipPackView-cardContainer-load" style={{ width: "100%" }}>
                        <div
                            data-testid="InternshipPackView-gridWrapper"
                            ref={viewType === 'grid' ? gridRef : null}
                            className={viewType === 'grid' ? Styles.gridWrapper : Styles.listWrapper}
                        >
                            {renderLabCards()}
                        </div>
                    </div>
                ) : (searchActive ? (
                    <div data-testid="InternshipPackView-emptyState-search" className={Styles.emptyState}>
                        <SearchOff sx={{ fontSize: 56 }} className={Styles.icon} />
                        <div data-testid="InternshipPackView-emptyState-search-message" className={Styles.enrollMessage}>{props.t('InternshipPack.InternshipPackView.message2')}</div>
                    </div>
                ) : (
                    <div data-testid="InternshipPackView-emptyState" className={`${Styles.emptyState} ${Styles.emptyStateBlue}`}>
                        {currentTab === 'my-internships' ? (
                            <School sx={{ fontSize: 56 }} className={Styles.icon} />
                        ) : currentTab === 'org' ? (
                            <AccountBalance sx={{ fontSize: 56 }} className={Styles.icon} />
                        ) : (
                            <PlaylistAddCheck sx={{ fontSize: 56 }} className={Styles.icon} />
                        )}
                        <div data-testid="InternshipPackView-emptyState-message" className={Styles.enrollMessage}>
                            {currentTab === 'my-internships'
                                ? props.t('InternshipPack.InternshipPackView.message3')
                                : currentTab === 'org'
                                    ? props.t('InternshipPack.InternshipPackView.message4')
                                    : props.t('InternshipPack.InternshipPackView.message1')
                            }
                        </div>
                    </div>
                ))}

                {/* Pagination */}
                {!loading && internships.length > 0 && (
                    <div data-testid="InternshipPackView-pagination" className={Styles.pageBtnContainer}>
                        <Pagination
                            count={totalPages}
                            color="primary"
                            variant='outlined'
                            page={pageNumber}
                            onChange={handlePageChange}
                            showFirstButton
                            showLastButton
                        />
                    </div>
                )}
            </div>
        </>
    );
}